import { Button, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { BellIcon } from "../../shared/components/icons";
import { parseSkillMessageData, type Notification } from "../types";
import styles from "./NotificationsBell.module.css";

interface NotificationsBellProps {
  readonly notifications: Notification[];
  readonly unreadCount: number;
  readonly onMarkRead: (id: number) => void;
  readonly onMarkAllRead: () => void;
  /** Invoked when a notification is opened — used to follow its deep-link. */
  readonly onOpen?: (notification: Notification) => void;
}

/** Bell + unread badge in the header; opens a popover listing recent notifications. */
export function NotificationsBell({
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onOpen,
}: NotificationsBellProps) {
  const handleOpen = (notification: Notification) => {
    if (!notification.read) {
      onMarkRead(notification.id);
    }
    onOpen?.(notification);
  };

  return (
    <Popover positioning="below-end" withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.bellButton}
          aria-label={
            unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"
          }
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className={styles.badge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverSurface>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Notifications</span>
            {unreadCount > 0 && (
              <Button appearance="subtle" size="small" onClick={onMarkAllRead}>
                Mark all read
              </Button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className={styles.empty}>You&apos;re all caught up.</p>
          ) : (
            <ul className={styles.list}>
              {notifications.map((n) => {
                const source = parseSkillMessageData(n)?.skillName;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`${styles.item} ${n.read ? "" : styles.unread}`}
                      onClick={() => handleOpen(n)}
                    >
                      <span className={styles.itemTitle}>{n.title}</span>
                      {source && <span className={styles.itemSource}>{source}</span>}
                      {n.body && <span className={styles.itemBody}>{n.body}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
